// The six kissaderby.fi cats. Cat i always runs in lane i (see CAT_IDS in race/types.ts).
//
// Looks come from the site's head photos (.taiga/knowledge/landscape/kissaderby-cats.md, the authority
// for facial markings) and the cat studies in .taiga/knowledge/ui. Bodies, builds and personalities
// are our extrapolation: the site shows only heads and names.
//
// Coat coordinates are the base cat's rest pose, in metres: +Y up, the cat faces +Z, its own right
// side is -X. In a frontal photo the viewer's left is therefore -X. Landmarks: nose tip (0, 0.243,
// 0.321), eyes (±0.018, 0.265, 0.289), ear tips (±0.039, 0.322, 0.262), crown (0, 0.30, 0.24),
// back ridge y ≈ 0.29, belly y ≈ 0.09, front legs z 0.04..0.16, hind legs z -0.26..-0.14,
// tail from z = -0.27 (base) to z = -0.62 (tip) at y ≈ 0.25.
import type { CatId } from '../race/types';
import { CAT_IDS } from '../race/types';

export type Vec3 = readonly [number, number, number];

/** Which colour a marking paints: the cat's white, its dark, its base coat, or an explicit hex. */
export type PatchColour = 'white' | 'dark' | 'base' | `#${string}`;

/**
 * One marking. Markings are painted in order, each over the result of the ones before.
 * - ellipsoid: inside the ellipsoid at `centre` with radii `size`.
 * - plane: the side of the plane through `centre` that the normal `size` points to.
 * - ring: a band `edge` wide around the surface of the ellipsoid (eyeliner, spectacles).
 */
export interface CoatPatch {
  shape: 'ellipsoid' | 'plane' | 'ring';
  centre: Vec3;
  size: Vec3;
  colour: PatchColour;
  /** Width of the soft edge, metres. */
  edge: number;
  /** How ragged the edge is, metres of noise displacement. */
  ragged?: number;
  /** Also paint the marking mirrored to the other side (x → -x). */
  mirror?: boolean;
  /** 0..1, how strongly the marking covers what is under it. */
  amount?: number;
}

export interface CoatSpec {
  /** Main coat colour and its lighter variation (mottling, underside). */
  base: string;
  baseLight: string;
  /** Tabby stripes, agouti tips and dark markings. */
  dark: string;
  /** White spotting. */
  white: string;
  /** 0..1 mackerel tabby stripe strength on body, legs, tail and forehead. */
  tabby: number;
  /** 0..1 agouti ticking, banded along each hair by the fur shells. */
  ticking: number;
  /** 0..1 lighter underside. */
  countershade: number;
  patches: CoatPatch[];
  nose: string;
  /** Inner ear skin. */
  earSkin: string;
  pads: string;
  /** Fur length at the back, metres. Face and legs are shorter. */
  furLength: number;
}

/**
 * Proportions as bone changes, applied on top of every clip.
 * Lengths stretch a bone chain without thickening it; scales grow a part uniformly.
 */
export interface BuildSpec {
  /** Uniform size of the whole cat. */
  size: number;
  /** Length of the legs (front and hind together, so the paws still meet the ground). */
  legLength: number;
  /** Length of the torso between shoulders and hips. */
  bodyLength: number;
  neckLength: number;
  tailLength: number;
  head: number;
  ears: number;
  /** Fullness of chest and belly. */
  girth: number;
  eyes: number;
}

export interface EyeSpec {
  /** Iris colour near the pupil and at the rim. */
  inner: string;
  outer: string;
  /** Pupil radius as a share of the iris (0..1). */
  pupil: number;
  /** 0 = a slit, 1 = round. */
  pupilRound: number;
  /** How much larger than the base model's iris, to fill the eye opening. */
  irisScale: number;
}

/** Character, used for gait flavour and idles. A proposal: the site gives no personalities. */
export interface Personality {
  /** One-line description for cards. */
  blurb: string;
  /** 0..1, bounce and eagerness in the gait. */
  energy: number;
  /** Mean seconds between ear twitches. */
  earTwitch: number;
  /** Metres of slow sideways drift in the lane (never leaving it). */
  wobble: number;
  /** Tail carriage while running, radians above the base pose. */
  tailLift: number;
  /** After finishing: relative chance to sit, groom or stand and look around. */
  settle: { sit: number; groom: number; idle: number };
}

export interface CatDef {
  id: CatId;
  lane: number;
  /** Full name, as the site spells it. */
  name: string;
  /** Compact name for tight rows and tags. */
  shortName: string;
  /** The site's accent colour for this cat, for the UI and track identification only. */
  accent: string;
  accentHue: number;
  coat: CoatSpec;
  build: BuildSpec;
  eyes: EyeSpec;
  personality: Personality;
}

const accent = (hue: number) => `hsl(${hue} 90% 70%)`;

export const CATS: readonly CatDef[] = [
  {
    id: 'nemo', lane: 0, name: 'Nemo Pongo', shortName: 'Nemo', accent: accent(28), accentHue: 28,
    coat: {
      base: '#c35a17', baseLight: '#e38a3a', dark: '#7e2f08', white: '#f3eee6',
      tabby: 0.8, ticking: 0.1, countershade: 0.45,
      patches: [
        // irregular white forehead mark, a little to the viewer's left
        { shape: 'ellipsoid', centre: [-0.003, 0.287, 0.283], size: [0.006, 0.013, 0.016], colour: 'white', edge: 0.003, ragged: 0.006 },
        // white muzzle and lower cheeks, more of it on the viewer's left
        { shape: 'ellipsoid', centre: [-0.004, 0.226, 0.302], size: [0.03, 0.019, 0.03], colour: 'white', edge: 0.005, ragged: 0.004 },
        { shape: 'ellipsoid', centre: [-0.016, 0.236, 0.283], size: [0.02, 0.014, 0.022], colour: 'white', edge: 0.005, ragged: 0.005 },
        // throat, chest and belly
        { shape: 'ellipsoid', centre: [0, 0.18, 0.225], size: [0.045, 0.06, 0.07], colour: 'white', edge: 0.012, ragged: 0.01 },
        { shape: 'ellipsoid', centre: [0, 0.14, 0.13], size: [0.05, 0.06, 0.08], colour: 'white', edge: 0.015, ragged: 0.01 },
        { shape: 'ellipsoid', centre: [0, 0.075, -0.04], size: [0.05, 0.05, 0.2], colour: 'white', edge: 0.015, ragged: 0.012 },
        // white socks, higher at the front
        { shape: 'plane', centre: [0, 0.085, 0.1], size: [0, -1, 0.25], colour: 'white', edge: 0.01, ragged: 0.012 },
        { shape: 'plane', centre: [0, 0.05, -0.2], size: [0, -1, -0.1], colour: 'white', edge: 0.008, ragged: 0.01 },
      ],
      nose: '#c76a5e', earSkin: '#d99a8c', pads: '#d98f86', furLength: 0.008,
    },
    build: { size: 1.04, legLength: 1.0, bodyLength: 1.02, neckLength: 1.0, tailLength: 1.0, head: 1.06, ears: 1.2, girth: 1.12, eyes: 1.04 },
    eyes: { inner: '#a8701e', outer: '#7a4a12', pupil: 0.5, pupilRound: 0.65, irisScale: 1.4 },
    personality: {
      blurb: 'The cheerful, bold household favourite.',
      energy: 0.8, earTwitch: 3.5, wobble: 0.22, tailLift: 0.35, settle: { sit: 2, groom: 1, idle: 1 },
    },
  },
  {
    id: 'sirius', lane: 1, name: 'Sirius Scout', shortName: 'Sirius', accent: accent(195), accentHue: 195,
    coat: {
      base: '#f1ede6', baseLight: '#fbf8f2', dark: '#141213', white: '#f4f0ea',
      tabby: 0, ticking: 0, countershade: 0.1,
      patches: [
        // black ear caps joined by a band over the crown
        { shape: 'ellipsoid', centre: [0.035, 0.307, 0.258], size: [0.022, 0.024, 0.022], colour: 'dark', edge: 0.004, ragged: 0.004, mirror: true },
        { shape: 'ellipsoid', centre: [0, 0.303, 0.245], size: [0.045, 0.011, 0.022], colour: 'dark', edge: 0.005, ragged: 0.005 },
        // the goatee: a separate black spot on the chin, slightly to the viewer's left
        { shape: 'ellipsoid', centre: [-0.003, 0.218, 0.296], size: [0.009, 0.008, 0.013], colour: 'dark', edge: 0.003, ragged: 0.002 },
        // small dark mark over the viewer's right eye
        { shape: 'ellipsoid', centre: [0.015, 0.281, 0.289], size: [0.004, 0.006, 0.007], colour: 'dark', edge: 0.002, ragged: 0.002 },
        // body: dark saddle patches and a dark tail with a white tip
        { shape: 'ellipsoid', centre: [0.01, 0.29, -0.01], size: [0.06, 0.035, 0.05], colour: 'dark', edge: 0.01, ragged: 0.015 },
        { shape: 'ellipsoid', centre: [-0.01, 0.27, -0.16], size: [0.075, 0.05, 0.07], colour: 'dark', edge: 0.01, ragged: 0.015 },
        { shape: 'ellipsoid', centre: [0.055, 0.24, 0.07], size: [0.03, 0.06, 0.025], colour: 'dark', edge: 0.008, ragged: 0.01 },
        { shape: 'plane', centre: [0, 0.25, -0.27], size: [0, 0, -1], colour: 'dark', edge: 0.01, ragged: 0.006 },
        { shape: 'plane', centre: [0, 0.25, -0.575], size: [0, 0, -1], colour: 'white', edge: 0.012, ragged: 0.006 },
      ],
      nose: '#e79a9c', earSkin: '#eeaaa8', pads: '#e7a1a0', furLength: 0.007,
    },
    build: { size: 0.98, legLength: 1.02, bodyLength: 1.03, neckLength: 1.04, tailLength: 1.0, head: 0.98, ears: 1.25, girth: 0.92, eyes: 1.05 },
    eyes: { inner: '#c8901e', outer: '#8f6416', pupil: 0.42, pupilRound: 0.55, irisScale: 1.42 },
    personality: {
      blurb: 'The alert, curious scout who sniffs out every ham.',
      energy: 0.9, earTwitch: 1.8, wobble: 0.28, tailLift: 0.45, settle: { sit: 1, groom: 1, idle: 3 },
    },
  },
  {
    id: 'tyyne', lane: 2, name: 'Tyyne', shortName: 'Tyyne', accent: accent(275), accentHue: 275,
    coat: {
      base: '#1b1516', baseLight: '#2c2222', dark: '#0f0b0b', white: '#f3efe9',
      tabby: 0, ticking: 0, countershade: 0,
      patches: [
        // narrow blaze up the nose, between the eyes, a touch to the viewer's right
        { shape: 'ellipsoid', centre: [0.002, 0.27, 0.306], size: [0.0045, 0.02, 0.02], colour: 'white', edge: 0.002, ragged: 0.002 },
        // white muzzle and chin, more on the viewer's right; no chin mark
        { shape: 'ellipsoid', centre: [0.004, 0.229, 0.302], size: [0.026, 0.019, 0.03], colour: 'white', edge: 0.004, ragged: 0.003 },
        { shape: 'ellipsoid', centre: [0.012, 0.238, 0.29], size: [0.016, 0.012, 0.02], colour: 'white', edge: 0.004, ragged: 0.003 },
        // bib, chest and a little belly
        { shape: 'ellipsoid', centre: [0, 0.19, 0.23], size: [0.036, 0.06, 0.06], colour: 'white', edge: 0.008, ragged: 0.008 },
        { shape: 'ellipsoid', centre: [0, 0.14, 0.135], size: [0.042, 0.06, 0.07], colour: 'white', edge: 0.012, ragged: 0.01 },
        { shape: 'ellipsoid', centre: [0, 0.08, 0.0], size: [0.035, 0.03, 0.09], colour: 'white', edge: 0.012, ragged: 0.01 },
        // white socks
        { shape: 'plane', centre: [0, 0.04, 0], size: [0, -1, 0], colour: 'white', edge: 0.008, ragged: 0.008 },
      ],
      nose: '#e2989b', earSkin: '#9b6a6a', pads: '#e0a0a0', furLength: 0.009,
    },
    build: { size: 0.95, legLength: 0.96, bodyLength: 0.97, neckLength: 0.95, tailLength: 1.0, head: 1.04, ears: 1.18, girth: 1.08, eyes: 1.06 },
    eyes: { inner: '#e2ee5e', outer: '#a8b62a', pupil: 0.38, pupilRound: 0.45, irisScale: 1.42 },
    personality: {
      blurb: 'Calm and serene, as her Finnish name says. Unflappable.',
      energy: 0.45, earTwitch: 6, wobble: 0.1, tailLift: 0.6, settle: { sit: 3, groom: 1, idle: 0.5 },
    },
  },
  {
    id: 'silkki', lane: 3, name: 'Silkkihienon Laterna Magica', shortName: 'Silkki', accent: accent(52), accentHue: 52,
    coat: {
      base: '#a85f27', baseLight: '#d39552', dark: '#2f1c10', white: '#ead0aa',
      tabby: 0.18, ticking: 1, countershade: 0.55,
      patches: [
        // cream chin, upper lip and throat
        { shape: 'ellipsoid', centre: [0, 0.226, 0.305], size: [0.012, 0.006, 0.016], colour: 'white', edge: 0.005, ragged: 0.002, amount: 0.75 },
        { shape: 'ellipsoid', centre: [0, 0.232, 0.305], size: [0.02, 0.01, 0.02], colour: '#d9a46c', edge: 0.006, ragged: 0.002, amount: 0.6 },
        { shape: 'ellipsoid', centre: [0, 0.2, 0.25], size: [0.025, 0.03, 0.03], colour: '#dca772', edge: 0.01, ragged: 0.005, amount: 0.8 },
        // light spectacles round each eye, then a dark liner
        { shape: 'ellipsoid', centre: [0.0184, 0.265, 0.2885], size: [0.017, 0.013, 0.012], colour: '#e7c08f', edge: 0.004, mirror: true, amount: 0.85 },
        { shape: 'ring', centre: [0.0184, 0.265, 0.2885], size: [0.0128, 0.0095, 0.009], colour: 'dark', edge: 0.0018, mirror: true, amount: 0.9 },
        // darker line along the spine and a dark tail tip
        { shape: 'ellipsoid', centre: [0, 0.3, -0.06], size: [0.022, 0.025, 0.3], colour: '#6b3a19', edge: 0.015, ragged: 0.006, amount: 0.6 },
        { shape: 'plane', centre: [0, 0.25, -0.56], size: [0, 0, -1], colour: 'dark', edge: 0.02, ragged: 0.004 },
      ],
      nose: '#b2503d', earSkin: '#d99484', pads: '#4a2f25', furLength: 0.0055,
    },
    build: { size: 1.0, legLength: 1.07, bodyLength: 1.04, neckLength: 1.06, tailLength: 1.04, head: 0.94, ears: 1.55, girth: 0.88, eyes: 1.0 },
    eyes: { inner: '#a4b8c8', outer: '#647e98', pupil: 0.36, pupilRound: 0.5, irisScale: 1.38 },
    personality: {
      blurb: 'An elegant, aristocratic show cat. Sleek, and a bit of a diva.',
      energy: 0.65, earTwitch: 4, wobble: 0.08, tailLift: 0.25, settle: { sit: 1, groom: 3, idle: 1 },
    },
  },
  {
    id: 'misse', lane: 4, name: 'Misse Perdita', shortName: 'Misse', accent: accent(330), accentHue: 330,
    coat: {
      base: '#f3f0eb', baseLight: '#fbf9f5', dark: '#2a1c16', white: '#f3f0eb',
      tabby: 0, ticking: 0, countershade: 0,
      patches: [
        // the dark head: crown, both ears and the viewer's-left half of the face
        { shape: 'ellipsoid', centre: [0, 0.285, 0.245], size: [0.06, 0.045, 0.065], colour: 'dark', edge: 0.006, ragged: 0.004 },
        { shape: 'ellipsoid', centre: [-0.03, 0.255, 0.275], size: [0.035, 0.045, 0.045], colour: 'dark', edge: 0.004, ragged: 0.003 },
        // ...split by a white blaze line down the nose to the viewer's right
        { shape: 'plane', centre: [0.004, 0.27, 0.3], size: [1, -0.12, 0.15], colour: 'white', edge: 0.003, ragged: 0.003 },
        { shape: 'ellipsoid', centre: [0.003, 0.226, 0.302], size: [0.026, 0.017, 0.03], colour: 'white', edge: 0.004, ragged: 0.002 },
        // the viewer's-right ear and back of the head stay dark
        { shape: 'ellipsoid', centre: [0.038, 0.306, 0.258], size: [0.012, 0.018, 0.014], colour: 'dark', edge: 0.004, ragged: 0.003 },
        { shape: 'ellipsoid', centre: [0.012, 0.3, 0.228], size: [0.04, 0.018, 0.03], colour: 'dark', edge: 0.006, ragged: 0.005 },
        { shape: 'ellipsoid', centre: [0.012, 0.302, 0.25], size: [0.04, 0.009, 0.026], colour: 'dark', edge: 0.005, ragged: 0.005 },
        // body: dark saddle, rump patch, hind-leg spot and a dark tail
        { shape: 'ellipsoid', centre: [0.015, 0.28, -0.04], size: [0.07, 0.045, 0.07], colour: 'dark', edge: 0.01, ragged: 0.016 },
        { shape: 'ellipsoid', centre: [-0.02, 0.255, -0.18], size: [0.075, 0.06, 0.07], colour: 'dark', edge: 0.01, ragged: 0.016 },
        { shape: 'ellipsoid', centre: [0.052, 0.09, -0.2], size: [0.016, 0.03, 0.016], colour: 'dark', edge: 0.005, ragged: 0.006 },
        { shape: 'plane', centre: [0, 0.25, -0.265], size: [0, 0, -1], colour: 'dark', edge: 0.012, ragged: 0.006 },
      ],
      nose: '#eaa3a6', earSkin: '#a6787a', pads: '#e6a3a3', furLength: 0.008,
    },
    build: { size: 0.98, legLength: 1.0, bodyLength: 1.0, neckLength: 1.0, tailLength: 1.0, head: 1.0, ears: 1.22, girth: 1.0, eyes: 1.04 },
    eyes: { inner: '#cfe0a6', outer: '#94b06a', pupil: 0.36, pupilRound: 0.35, irisScale: 1.4 },
    personality: {
      blurb: 'Mysterious and unpredictable. Perdita to Nemo Pongo\'s Pongo.',
      energy: 0.7, earTwitch: 2.5, wobble: 0.3, tailLift: 0.5, settle: { sit: 1, groom: 2, idle: 2 },
    },
  },
  {
    id: 'growl', lane: 5, name: 'Kharn-ka Growltiger', shortName: 'Growltiger', accent: accent(120), accentHue: 120,
    coat: {
      base: '#c39a62', baseLight: '#e6c891', dark: '#5f4428', white: '#f3e7d2',
      tabby: 0.22, ticking: 0.75, countershade: 0.6,
      patches: [
        { shape: 'ellipsoid', centre: [0, 0.226, 0.304], size: [0.016, 0.008, 0.018], colour: 'white', edge: 0.005, ragged: 0.002, amount: 0.8 },
        { shape: 'ellipsoid', centre: [0, 0.234, 0.305], size: [0.022, 0.011, 0.02], colour: '#ecd6ad', edge: 0.006, ragged: 0.002, amount: 0.7 },
        { shape: 'ellipsoid', centre: [0, 0.2, 0.25], size: [0.028, 0.035, 0.035], colour: '#ead3a8', edge: 0.01, ragged: 0.005, amount: 0.8 },
        { shape: 'ellipsoid', centre: [0.0184, 0.265, 0.2885], size: [0.018, 0.014, 0.012], colour: '#f0dcb4', edge: 0.004, mirror: true, amount: 0.8 },
        { shape: 'ring', centre: [0.0184, 0.265, 0.2885], size: [0.0135, 0.0105, 0.009], colour: 'dark', edge: 0.0016, mirror: true, amount: 0.8 },
        { shape: 'plane', centre: [0, 0.25, -0.55], size: [0, 0, -1], colour: 'dark', edge: 0.03, ragged: 0.004, amount: 0.85 },
      ],
      nose: '#a8584a', earSkin: '#dc9c90', pads: '#5a3b2c', furLength: 0.0065,
    },
    build: { size: 1.0, legLength: 1.06, bodyLength: 1.03, neckLength: 1.04, tailLength: 1.0, head: 0.98, ears: 1.45, girth: 0.94, eyes: 1.14 },
    eyes: { inner: '#5a3812', outer: '#2e1c08', pupil: 0.72, pupilRound: 1, irisScale: 1.46 },
    personality: {
      blurb: 'The swaggering tough guy. Named after the terror of the river in Old Possum.',
      energy: 1, earTwitch: 3, wobble: 0.18, tailLift: 0.3, settle: { sit: 1, groom: 0.5, idle: 3 },
    },
  },
];

/** The cat for an id. */
export function catById(id: CatId): CatDef {
  const c = CATS.find((x) => x.id === id);
  if (!c) throw new Error(`cats: unknown cat "${id}"`);
  return c;
}

/** The cat in a lane (0..5). */
export function catInLane(lane: number): CatDef {
  const c = CATS[lane];
  if (!c) throw new Error(`cats: no cat in lane ${lane}`);
  return c;
}

// Keep the table and the race types in step.
if (CATS.some((c, i) => c.lane !== i || c.id !== CAT_IDS[i])) throw new Error('cats: CATS must follow CAT_IDS lane order');

/** Every cat is drawn this much bigger than its build so it reads clearly from the race cameras
 *  (the base cat is about 0.5 m long; this makes it about 0.8 m, still well inside a 2.4 m lane). */
export const CAT_DISPLAY_SCALE = 1.6;
